import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { TableColumn } from '@shared/interface/table.interface';
import { BaseDatasource } from '@shared/base/base.datasource';
import { IListRetailer } from '@models/retailer.model';
import { BehaviorSubject, Subscription } from 'rxjs';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { IGenericValueDisplay } from '@shared/interface/generic';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseTableService } from '@shared/base/base-table-service.service';
import { UrlUtilsService } from '@utils/url-utils.service';
import { UtilitiesService } from '@services/utilities.service';
import { RolePrivilegeService } from '@services/role-privilege.service';
import { FilterService } from '@services/filter.service';
import { RetailerService } from '../../retailer.service';
import { API } from '@config/constants/api.constant';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { TerverifikasiTableColumns } from '../../retailer.data';
import { IDataRetailerList } from '../../retailers.interface';
import { InputSelectInterface } from '@shared/components/form/input-select/input-select.interface';

@Component({
  selector: 'app-retailer-list',
  templateUrl: './retailer-list.component.html',
  styleUrls: ['./retailer-list.component.scss'],
})
export class RetailerListComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort, { static: false }) matSort: MatSort;

  @Input() headerType: string = 'filter'; // Filter or Counting
  @Input() url: string = API.LIST_RETAILER_ACTIVE;
  @Input() tableColumns: TableColumn[] = TerverifikasiTableColumns;
  @Input() partnershipFilter: boolean = false;
  @Input() dataList: IDataRetailerList = {} as IDataRetailerList;
  @Output() totalCount = new EventEmitter();

  STRING_CONSTANTS = STRING_CONSTANTS;
  iconNone = STRING_CONSTANTS.ICON.IC_RETAIL_NONE;
  isLoading = new BehaviorSubject(false);

  // table
  displayedColumns: string[];
  baseDatasource: BaseDatasource<IListRetailer>;

  string_filter?: string;
  checkActive: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isOpenFilter: boolean = false;
  isActiveFilter: boolean = false;

  filterForm: FormGroup = this.formBuilder.group({
    type: [''],
    manager_id: [''],
    area_id: [''],
    sub_area_id: [''],
    is_mac: [''],
  });

  listRetailerAreaSubject = new BehaviorSubject([] as InputSelectInterface[]);
  listRetailerSubAreaSubject = new BehaviorSubject([] as InputSelectInterface[]);

  filterRetailerIsMac: IGenericValueDisplay[] = [];

  privilageDetailRegistered: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private unsubscribe: Subscription[] = [];

  get headerTypeFilter() {
    return this.headerType === 'filter';
  }

  get headerTypeCounting() {
    return this.headerType === 'counting';
  }

  get SubAreaFilterControl() {
    return <FormControl>this.filterForm.get('sub_area_id');
  }

  constructor(
    private activeRoute: ActivatedRoute,
    private router: Router,
    private baseTable: BaseTableService<IListRetailer>,
    private paginationService: UrlUtilsService,
    public utilities: UtilitiesService,
    private formBuilder: FormBuilder,
    private rolePrivilegeService: RolePrivilegeService,
    public filterService: FilterService,
    private retailerService: RetailerService
  ) {
    this.initFilter();
  }

  ngOnInit() {
    this.privilageDetailRegistered.next(this.rolePrivilegeService.checkPrivilege('RETAILER', 'LIST_RETAILER_TERVERIFIKASI', 'CTA_VIEW_DETAIL'));
    this.setTableData();
    this.queryHandler();
    this.getListFilter();
    this.baseDatasource.totalItem$.subscribe((value) => {
      this.totalCount.emit(value);
    });

    const area = this.filterForm.value.area_id;
    const subArea = this.filterForm.value.sub_area_id;
    if (area || subArea) {
      this.getRetailerSubArea(area);
    }
  }

  initFilter(value = { type: '', manager_id: '', area_id: '', is_mac: '', sub_area_id: '' }) {
    const { type, manager_id, area_id, is_mac, sub_area_id } = value;
    this.filterForm.patchValue({
      type,
      manager_id,
      area_id,
      is_mac,
      sub_area_id,
    });

    this.SubAreaFilterControl.disable();
  }

  getListFilter() {
    this.getRetailerArea();
    this.getFilterIsMac();
  }

  getFilterIsMac() {
    this.filterRetailerIsMac = [
      {
        value: 'true',
        display: 'MAC',
      },
      {
        value: 'false',
        display: 'Tidak Ada',
      },
    ];
  }

  getRetailerArea() {
    const _dataSubs = this.retailerService.getRetailerArea().subscribe((resp) => {
      if (!resp) return;
      const _data = this.retailerService.mapInputSelectRetailerArea(resp.data);
      this.listRetailerAreaSubject.next(_data);
    });

    this.unsubscribe.push(_dataSubs);
  }

  getRetailerSubArea(areaID: string) {
    this.isLoading.next(true);
    this.SubAreaFilterControl.disable();

    const _dataSubs = this.retailerService.getRetailerSubArea(areaID).subscribe((resp) => {
      if (!resp) return;
      const _data = this.retailerService.mapInputSelectRetailerArea(resp.data);
      this.listRetailerSubAreaSubject.next(_data);
      this.isLoading.next(false);
      this.SubAreaFilterControl.enable();
    });

    this.unsubscribe.push(_dataSubs);
  }

  setTableData() {
    const _dataSubs = this.baseTable.responseDatabase.subscribe((resp) => (this.baseDatasource = resp));

    this.tableColumns = this.utilities.privilegeTableColumns(this.privilageDetailRegistered.value, this.tableColumns);
    this.displayedColumns = this.tableColumns.map((head) => head.key);

    this.unsubscribe.push(_dataSubs);
  }

  sortTable(param: Sort) {
    return this.retailerService.sortTable(this.tableColumns, param);
  }

  changePageEvent($event: BaseDatasource<any>) {
    this.filterService.changePageEvent($event, this.string_filter ?? '');
  }

  handleActionDetail(id: string) {
    if (Object.keys(this.dataList).length) {
      const { status, view, tab } = this.dataList;
      if (status === 'pending') {
        return this.router.navigate([`/retailer/detail/${status}/${tab}/${view}/` + id]).then();
      } else {
        return this.router.navigate([`/retailer/detail/${tab}/${status}/` + id]).then();
      }
    } else {
      return this.router.navigate([`/retailer/detail/active/terverifikasi/` + id]).then();
    }
  }

  onSearch(event: string) {
    this.checkActive.next(!this.checkActive.value);
    this.filterService.onSearch(event);
    this.string_filter = '';
  }

  queryHandler() {
    this.activeRoute.queryParams.subscribe((data) => {
      const { string_filter, type, manager_id, area_id, is_mac, sub_area_id } = data;

      this.string_filter = string_filter;
      this.initFilter({ type, manager_id, area_id, is_mac, sub_area_id });
      this.isActiveFilter = !!(type || manager_id || area_id || is_mac);

      const param = this.paginationService.sliceQueryParams() ?? '';
      this.baseTable.loadDataTable(this.url, param);
    });
  }

  handleOpenFilter = () => {
    this.isOpenFilter = !this.isOpenFilter;
    const subArea = this.filterForm.value.sub_area_id;
    if (subArea !== null && subArea !== '') {
      this.SubAreaFilterControl.enable();
    }
  };

  handleSubmitFilter = () => {
    this.isActiveFilter = true;
    this.isOpenFilter = false;
    this.checkActive.next(!this.checkActive.value);
    this.filterService.submitFilter(this.filterForm);
  };

  handleResetFilter = () => {
    this.isActiveFilter = false;
    this.isOpenFilter = false;
    const _keyFilter = ['type', 'manager_id', 'area_id', 'sub_area_id', 'is_mac'];
    this.filterService.resetFilter(this.filterForm, _keyFilter);
  };

  handleChangeAreaFilter(e: string) {
    this.SubAreaFilterControl.setValue(null);
    if (!e) return;
    return this.getRetailerSubArea(e);
  }

  ngOnDestroy(): void {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
